import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { PRICING as STATIC_PRICING } from "@/content/pricing";
import { CONTACT as STATIC_CONTACT } from "@/content/contact";

/**
 * ContentProvider — pont entre le CMS admin (MongoDB) et les pages publiques.
 *
 * Charge en parallèle : pricing · contact · reviews · media manifest.
 * Expose les données via useContent(). Fallback silencieux sur les fichiers
 * statiques `/content/*.js` si l'API échoue (site continue de fonctionner).
 *
 * Media manifest → liste de fichiers uploadés dans GridFS par l'admin. Le
 * helper `mediaUrl(basename, staticFallback)` retourne l'URL backend si le
 * fichier est présent, sinon le chemin statique local.
 */

const ContentContext = createContext(null);

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "refuge";

const isEmpty = (obj) => !obj || (typeof obj === "object" && Object.keys(obj).length === 0);

export function ContentProvider({ children }) {
  const [pricing, setPricing] = useState(STATIC_PRICING);
  const [contact, setContact] = useState(STATIC_CONTACT);
  const [reviews, setReviews] = useState({ reviews: [], stats: [] });
  const [content, setContent] = useState({});
  const [manifest, setManifest] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.getPricing(SITE),
      api.getContact(SITE),
      api.getReviews(SITE),
      api.getMediaManifest(SITE),
      api.getContent(SITE),
    ]).then(([p, c, r, m, ct]) => {
      if (!alive) return;
      if (p.status === "fulfilled" && !isEmpty(p.value)) setPricing({ ...STATIC_PRICING, ...p.value });
      if (c.status === "fulfilled" && !isEmpty(c.value)) setContact({ ...STATIC_CONTACT, ...c.value });
      if (r.status === "fulfilled" && r.value && Array.isArray(r.value.reviews)) setReviews(r.value);
      if (ct.status === "fulfilled" && ct.value && typeof ct.value === "object") setContent(ct.value);
      if (m.status === "fulfilled" && m.value) {
        // Compat : le manifest peut être un objet {filename: uploadDate}
        // (nouveau format, avec cache-busting) ou une liste [filename] (ancien).
        if (Array.isArray(m.value)) {
          const obj = {};
          m.value.forEach((f) => { obj[f] = ""; });
          setManifest(obj);
        } else if (typeof m.value === "object") {
          setManifest(m.value);
        }
      }
      setReady(true);
    });
    return () => { alive = false; };
  }, []);

  const mediaUrl = useMemo(() => {
    return (staticPath) => {
      // Extrait le basename d'un chemin comme "/media/images/experiences/bain-de-fleurs.png"
      const basename = staticPath?.split("/").pop();
      if (!basename || !(basename in manifest)) return staticPath;
      // Cache-buster basé sur la date d'upload : chaque nouvel upload via
      // l'admin produit une URL unique qui contourne le cache Cloudflare.
      const version = manifest[basename];
      const v = version
        ? encodeURIComponent(version).replace(/[^A-Za-z0-9]/g, "").slice(0, 24)
        : "1";
      return `${API_ROOT}/media/${encodeURIComponent(basename)}?v=${v}`;
    };
  }, [manifest]);

  const buildWhatsAppUrl = useMemo(() => {
    const num = contact.whatsappNumber || STATIC_CONTACT.whatsappNumber;
    const fallback = contact.whatsappDefaultMessage || STATIC_CONTACT.whatsappDefaultMessage;
    return (message) => {
      const text = message ? encodeURIComponent(message) : fallback;
      return `https://wa.me/${num}?text=${text}`;
    };
  }, [contact]);

  /**
   * saveText(path, value) — mise à jour d'un texte via inline edit admin.
   *
   * `path` est une notation pointée type "experiences.petit-dejeuner.sub"
   * ou "experiences.petit-dejeuner.essential.2" (index numérique pour tableaux).
   * Fait un update optimistic (state React) puis PUT /admin/content complet.
   */
  const setDeep = (obj, path, val) => {
    const keys = path.split(".");
    const clone = JSON.parse(JSON.stringify(obj || {}));
    let cur = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      const nextK = keys[i + 1];
      const asArray = /^\d+$/.test(nextK);
      if (cur[k] === undefined || cur[k] === null) cur[k] = asArray ? [] : {};
      cur = cur[k];
    }
    const last = keys[keys.length - 1];
    if (/^\d+$/.test(last)) cur[parseInt(last, 10)] = val;
    else cur[last] = val;
    return clone;
  };
  const saveText = async (path, val) => {
    const next = setDeep(content, path, val);
    setContent(next);            // optimistic
    try {
      await api.putContent(SITE, next);
    } catch (e) {
      // rollback simple : on recharge
      const fresh = await api.getContent(SITE).catch(() => null);
      if (fresh && typeof fresh === "object") setContent(fresh);
      throw e;
    }
  };

  const value = { pricing, contact, reviews, content, manifest, mediaUrl, buildWhatsAppUrl, ready, saveText };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent() {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used inside <ContentProvider>");
  return ctx;
}
